import React, { useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import {
  getInventoryPerformanceStatsByProjectId,
  getInventoryStatsByProjectId
} from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../../contexts/ProjectsContext';

const InventoryGraphs = ({ projectID, month }) => {
  const userID = Number(Cookies.get('userId'));
  const { projectsForDropdown } = useProjectsList();

  // API Queries
  const {
    data: userStats,
    refetch: refetStats,
    isLoading: isLoadingStats
  } = useQuery({
    queryKey: ['userStats', projectID, month],
    queryFn: () => getInventoryPerformanceStatsByProjectId(projectID, month),
    enabled: !!projectID && !!month,
    onSuccess: (data) => {
      mixpanel.track('Inventory Stats API Success', {
        user_id: userID,
        project_id: projectID,
        month: month,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Inventory Stats API Failed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: InvStats,
    refetch: refetInvStats,
    isLoading: isLoadingInvStats
  } = useQuery({
    queryKey: ['InvStats', projectID, month],
    queryFn: () => getInventoryStatsByProjectId(projectID, month),
    enabled: !!projectID,
    onSuccess: (data) => {
      mixpanel.track('Inventory Stats API Success', {
        user_id: userID,
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Inventory Stats API Failed', {
        user_id: userID,
        project_id: projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Refetch data when project or month changes
  useEffect(() => {
    if (projectID && month) {
      refetStats();
      refetInvStats();
    }
  }, [projectID, month, refetStats, refetInvStats]);

  // Chart component functions
  const CircularProgress = ({ percentage, title, color, size = 120 }) => {
    const radius = (size - 20) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <svg className="transform -rotate-90" width={size} height={size}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="10"
              fill="transparent"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-gray-700">
              {percentage > 0 ? `${percentage}%` : '---'}
            </span>
          </div>
        </div>
        <span className="text-sm font-medium text-gray-600 mt-2">{title}</span>
      </div>
    );
  };

  const TopRoundedBar1 = (props) => {
    const { x, y, width, height, fill, payload } = props;
    if (payload.value === 0) return null;
    const radius = 6;
    return (
      <path
        d={`
          M${x},${y + height}
          V${y + radius}
          Q${x},${y} ${x + radius},${y}
          H${x + width - radius}
          Q${x + width},${y} ${x + width},${y + radius}
          V${y + height}
          Z
        `}
        style={{ cursor: 'pointer' }}
        fill={fill}
      />
    );
  };

  const renderCustomLabelForPie = ({
    value,
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index
  }) => {
    if (value === 0) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#000"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 12, fontWeight: 500 }}>
        {value}%
      </text>
    );
  };

  // Data processing
  const InvPieData = [
    { name: 'Less than 100%', value: Math.round(InvStats?.redCount || 0), color: '#f25555' },
    { name: '100% to 125%', value: Math.round(InvStats?.orangeCount || 0), color: '#eda24b' },
    { name: '125% to 150%', value: Math.round(InvStats?.yellowCount || 0), color: '#f1e567' },
    { name: 'Greater than 150%', value: Math.round(InvStats?.greenCount || 0), color: '#80cf9c' }
  ];

  const averagePOData = [
    {
      name: 'Current Month',
      value: Math.round(Number(InvStats?.currentMonthOrdersValue || 0)),
      color: '#3B82F6'
    },
    {
      name: `Project - ${projectsForDropdown?.find((item) => item.id === Number(projectID))?.label || 'Current'}`,
      value: Math.round(Number(InvStats?.totalOrdersValue || 0)),
      color: '#10B981'
    }
  ];

  const processedPOData = averagePOData.map((item) => ({
    ...item,
    displayValue: [InvStats?.currentMonthOrdersValue, InvStats?.totalOrdersValue].some(
      (v) => Number(v || 0) >= 50
    )
      ? Math.min(item.value, 50)
      : item.value,
    actualValue: item.value
  }));

  const hasPOData = [InvStats?.currentMonthOrdersValue, InvStats?.totalOrdersValue].some(
    (n) => Number(n || 0) > 0
  );

  const hasPieData = InvPieData.some((item) => item.value > 0);

  return (
    <div className="flex flex-col gap-5 transition-all duration-300 ease-in-out w-full">
      {/* Performance Overview */}
      <div className="p-6 rounded-xl shadow bg-white">
        <h3 className="text-lg font-semibold mb-6 text-gray-800">Performance Overview</h3>

        <div className="flex justify-around items-center">
          <CircularProgress
            percentage={Math.round(userStats?.dailyTaskPercentage || 0)}
            title="Tasks"
            color="#3B82F6"
            size={140}
          />
          <CircularProgress
            percentage={userStats?.performance || 0}
            title="Performance"
            color="#F59E0B"
            size={140}
          />
        </div>
      </div>

      {/* Stock Criticality Split */}
      <div className="flex rounded-lg shadow bg-white gap-4 p-6 flex-col items-center w-full max-w-4xl mx-auto">
        <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
          STOCK CRITICALITY SPLIT
        </h3>
        <div className="bg-[#346C75] h-[2px] w-[80%] mb-4" />

        <div className="w-full flex flex-col items-center">
          {hasPieData ? (
            <>
              <div className="flex justify-center mb-6">
                <PieChart width={240} height={240}>
                  <Pie
                    data={InvPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    dataKey="value"
                    label={renderCustomLabelForPie}
                    labelLine={false}>
                    {InvPieData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </div>

              {/* Legend */}
              <div className="flex gap-6 flex-wrap justify-center">
                {InvPieData.map((entry, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div
                      className="w-4 h-3 rounded-sm"
                      style={{ backgroundColor: entry.color }}></div>
                    <span className="text-xs text-gray-700">{entry.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500 mt-4 h-[10rem] flex items-center justify-center">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Average Purchase Order */}
      <div className="p-4 w-full max-w-4xl mx-auto bg-white rounded-xl flex flex-col justify-start items-center shadow gap-3">
        <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
          AVERAGE PO (PURCHASE ORDER)
        </h3>
        <div className="bg-black h-[2px] w-[80%] mb-4" />

        <ResponsiveContainer width="100%" height={300}>
          {hasPOData ? (
            <BarChart data={processedPOData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis
                type="number"
                domain={
                  [InvStats?.currentMonthOrdersValue, InvStats?.totalOrdersValue].some(
                    (v) => Number(v || 0) >= 50
                  )
                    ? [0, 50]
                    : [0, 'dataMax']
                }
                allowDataOverflow={false}
                allowDecimals={false}
              />
              <Bar dataKey="displayValue" shape={<TopRoundedBar1 />} isAnimationActive={false}>
                {processedPOData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="actualValue"
                  content={({ x, y, width, height, value }) => {
                    if (!value) return null;
                    const exceedsMax =
                      [InvStats?.currentMonthOrdersValue, InvStats?.totalOrdersValue].some(
                        (v) => Number(v || 0) >= 50
                      ) && value > 50;
                    return (
                      <text
                        x={x + width / 2}
                        y={exceedsMax ? y + height / 2 : y + 15}
                        textAnchor="middle"
                        style={{ fontSize: 12, fill: '#fff', fontWeight: 500 }}>
                        {value}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
              No data available
            </div>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default InventoryGraphs;
